import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Avatar,
  Box,
  Button,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import { authFetch } from "../../utils/AuthFetch";

export default function EditProfile({
  user,
  mode,
  onCancel,
  onUpdated,
  onError,
}) {
  const fileInputRef = useRef(null);

  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    phone: "",
    address: "",
    documentType: "",
    documentNumber: "",
  });

  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [previewPhoto, setPreviewPhoto] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (user) {
      setForm({
        firstName: user.first_name || "",
        lastName: user.last_name || "",
        phone: user.phone || "",
        address: user.profile?.address || "",
        documentType: user.profile?.document_type || "",
        documentNumber: user.profile?.document_number || "",
      });
      setSelectedPhoto(null);
      setPreviewPhoto(user.profile?.profile_photo || "");
    }
  }, [user]);

  useEffect(() => {
    return () => {
      if (previewPhoto && selectedPhoto) {
        URL.revokeObjectURL(previewPhoto);
      }
    };
  }, [previewPhoto, selectedPhoto]);

  const displayPhoto = useMemo(() => {
    if (selectedPhoto && previewPhoto) return previewPhoto;
    return user?.profile?.profile_photo || "";
  }, [previewPhoto, selectedPhoto, user]);

  const handleChange = (field) => (event) => {
    setForm((prev) => ({
      ...prev,
      [field]: event.target.value,
    }));
  };

  const handlePickPhoto = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handlePhotoChange = (event) => {
    const file = event.target.files?.[0];

    if (!file) return;

    if (previewPhoto && selectedPhoto) {
      URL.revokeObjectURL(previewPhoto);
    }

    const objectUrl = URL.createObjectURL(file);
    setSelectedPhoto(file);
    setPreviewPhoto(objectUrl);
  };

  const handleSave = async () => {
    if (mode === "personal") {
      if (!form.firstName.trim() || !form.lastName.trim() || !form.phone.trim()) {
        onError("Nombre, apellido y teléfono son obligatorios");
        return;
      }
    }

    if (mode === "billing") {
      if (!form.documentType.trim() || !form.documentNumber.trim()) {
        onError("Tipo y número de documento son obligatorios");
        return;
      }
    }

    if (mode === "photo" && !selectedPhoto) {
      onError("Debes seleccionar una foto");
      return;
    }

    setSaving(true);

    try {
      const formData = new FormData();

      if (mode === "personal") {
        formData.append("first_name", form.firstName.trim());
        formData.append("last_name", form.lastName.trim());
        formData.append("phone", form.phone.trim());
        formData.append("address", form.address.trim());
      }

      if (mode === "billing") {
        formData.append("document_type", form.documentType);
        formData.append("document_number", form.documentNumber.trim());
      }

      if (mode === "photo" && selectedPhoto) {
        formData.append("profile_photo", selectedPhoto);
      }

      const response = await authFetch("/api/gym/profile/update/", {
        method: "PATCH",
        body: formData,
      });

      const data = await response.json();

      if (!response.ok) {
        onError(data.error || "No se pudo actualizar el perfil");
        return;
      }

      onUpdated(data.user, data.message || "Perfil actualizado correctamente");
    } catch {
      onError("Ocurrió un error al conectar con el servidor");
    } finally {
      setSaving(false);
    }
  };

  if (mode === "photo") {
    return (
      <Box
        sx={{
          borderRadius: "22px",
          border: "1px solid rgba(17,17,17,0.08)",
          p: { xs: 2.2, md: 3 },
        }}
      >
        <Stack spacing={2.2} alignItems="center">
          <Box sx={{ position: "relative" }}>
            <Avatar
              src={displayPhoto}
              sx={{
                width: 120,
                height: 120,
                fontSize: 36,
                fontWeight: 800,
                bgcolor: "#111111",
                color: "#ffffff",
                boxShadow: "0 16px 36px rgba(0,0,0,0.15)",
              }}
            >
              {form.firstName?.[0] || user?.first_name?.[0]}
            </Avatar>

            <IconButton
              onClick={handlePickPhoto}
              sx={{
                position: "absolute",
                right: -4,
                bottom: -4,
                width: 38,
                height: 38,
                bgcolor: "#ffffff",
                color: "#111111",
                border: "1px solid rgba(17,17,17,0.12)",
                boxShadow: "0 12px 30px rgba(0,0,0,0.16)",
                "&:hover": {
                  bgcolor: "#ffffff",
                  color: "#e50914",
                  borderColor: "#e50914",
                },
              }}
            >
              <EditOutlinedIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Box>

          <Box sx={{ textAlign: "center" }}>
            <Typography
              sx={{
                color: "#111111",
                fontWeight: 900,
                fontSize: { xs: 22, md: 26 },
                mb: 0.7,
              }}
            >
              Actualizar foto de perfil
            </Typography>

            <Typography
              sx={{
                color: "rgba(17,17,17,0.62)",
                fontSize: 14.5,
                lineHeight: 1.7,
                maxWidth: 520,
              }}
            >
              Elige una imagen PNG, JPG o WEBP para actualizar tu foto de perfil.
            </Typography>
          </Box>

          <input
            ref={fileInputRef}
            type="file"
            accept="image/png,image/jpeg,image/jpg,image/webp"
            hidden
            onChange={handlePhotoChange}
          />

          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.2}>
            <Button
              variant="contained"
              onClick={handlePickPhoto}
              sx={{
                textTransform: "none",
                fontWeight: 800,
                borderRadius: "14px",
                py: 1.1,
                px: 2.4,
                color: "#ffffff",
                background:
                  "linear-gradient(135deg, #111111 0%, #2b2b2b 45%, #e50914 100%)",
                boxShadow: "0 18px 50px rgba(0,0,0,0.18)",
                "&:hover": {
                  filter: "brightness(1.05)",
                },
              }}
            >
              Elegir imagen
            </Button>

            <Button
              variant="outlined"
              onClick={onCancel}
              disabled={saving}
              sx={{
                textTransform: "none",
                fontWeight: 800,
                borderRadius: "14px",
                py: 1.1,
                px: 2.4,
                color: "#111111",
                borderColor: "rgba(17,17,17,0.14)",
                "&:hover": {
                  borderColor: "#e50914",
                  color: "#e50914",
                  bgcolor: "rgba(229,9,20,0.05)",
                },
              }}
            >
              Cancelar
            </Button>

            <Button
              variant="contained"
              onClick={handleSave}
              disabled={saving}
              sx={{
                textTransform: "none",
                fontWeight: 800,
                borderRadius: "14px",
                py: 1.1,
                px: 2.4,
                color: "#ffffff",
                background:
                  "linear-gradient(135deg, #5f0008 0%, #a80d19 35%, #df2632 62%, #161616 100%)",
                boxShadow: "0 18px 50px rgba(229,9,20,0.22)",
                "&:hover": {
                  filter: "brightness(1.05)",
                  boxShadow: "0 22px 56px rgba(229,9,20,0.26)",
                },
                "&.Mui-disabled": {
                  color: "#ffffff",
                  opacity: 0.75,
                },
              }}
            >
              {saving ? "Guardando..." : "Guardar foto"}
            </Button>
          </Stack>
        </Stack>
      </Box>
    );
  }

  if (mode === "billing") {
    return (
      <Box
        sx={{
          borderRadius: "22px",
          border: "1px solid rgba(17,17,17,0.08)",
          p: { xs: 2.2, md: 3 },
        }}
      >
        <Stack spacing={2}>
          <Box>
            <Typography
              sx={{
                color: "#111111",
                fontWeight: 900,
                fontSize: { xs: 22, md: 26 },
                mb: 0.7,
              }}
            >
              Completar facturación
            </Typography>

            <Typography
              sx={{
                color: "rgba(17,17,17,0.62)",
                fontSize: 14.5,
                lineHeight: 1.7,
              }}
            >
              Registra tu tipo y número de documento. Una vez guardados, estos datos ya no podrán editarse desde aquí.
            </Typography>
          </Box>

          <FormControl fullWidth>
            <InputLabel id="document-type-label">Tipo de documento</InputLabel>
            <Select
              labelId="document-type-label"
              label="Tipo de documento"
              value={form.documentType}
              onChange={handleChange("documentType")}
              sx={{
                borderRadius: "16px",
              }}
            >
              <MenuItem value="cedula">Cédula</MenuItem>
              <MenuItem value="ruc">RUC</MenuItem>
              <MenuItem value="passport">Pasaporte</MenuItem>
            </Select>
          </FormControl>

          <TextField
            fullWidth
            label="Número de documento"
            value={form.documentNumber}
            onChange={handleChange("documentNumber")}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "16px",
              },
            }}
          />

          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.2} sx={{ pt: 0.5 }}>
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={saving}
              sx={{
                textTransform: "none",
                fontWeight: 800,
                borderRadius: "14px",
                py: 1.1,
                px: 2.4,
                color: "#ffffff",
                background:
                  "linear-gradient(135deg, #5f0008 0%, #a80d19 35%, #df2632 62%, #161616 100%)",
                boxShadow: "0 18px 50px rgba(229,9,20,0.22)",
                "&:hover": {
                  filter: "brightness(1.05)",
                  boxShadow: "0 22px 56px rgba(229,9,20,0.26)",
                },
                "&.Mui-disabled": {
                  color: "#ffffff",
                  opacity: 0.75,
                },
              }}
            >
              {saving ? "Guardando..." : "Guardar facturación"}
            </Button>

            <Button
              variant="outlined"
              onClick={onCancel}
              disabled={saving}
              sx={{
                textTransform: "none",
                fontWeight: 800,
                borderRadius: "14px",
                py: 1.1,
                px: 2.4,
                color: "#111111",
                borderColor: "rgba(17,17,17,0.14)",
                "&:hover": {
                  borderColor: "#e50914",
                  color: "#e50914",
                  bgcolor: "rgba(229,9,20,0.05)",
                },
              }}
            >
              Cancelar
            </Button>
          </Stack>
        </Stack>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        borderRadius: "22px",
        border: "1px solid rgba(17,17,17,0.08)",
        p: { xs: 2.2, md: 3 },
      }}
    >
      <Stack spacing={2}>
        <Box>
          <Typography
            sx={{
              color: "#111111",
              fontWeight: 900,
              fontSize: { xs: 22, md: 26 },
              mb: 0.7,
            }}
          >
            Editar información personal
          </Typography>

          <Typography
            sx={{
              color: "rgba(17,17,17,0.62)",
              fontSize: 14.5,
              lineHeight: 1.7,
            }}
          >
            Aquí puedes actualizar tus datos personales. Los datos de facturación se gestionan por separado.
          </Typography>
        </Box>

        <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
          <TextField
            fullWidth
            label="Nombre"
            value={form.firstName}
            onChange={handleChange("firstName")}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "16px",
              },
            }}
          />

          <TextField
            fullWidth
            label="Apellido"
            value={form.lastName}
            onChange={handleChange("lastName")}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "16px",
              },
            }}
          />
        </Stack>

        <TextField
          fullWidth
          label="Correo electrónico"
          value={user.email || ""}
          disabled
          sx={{
            "& .MuiOutlinedInput-root": {
              borderRadius: "16px",
            },
          }}
        />

        <TextField
          fullWidth
          label="Número de teléfono"
          value={form.phone}
          onChange={handleChange("phone")}
          sx={{
            "& .MuiOutlinedInput-root": {
              borderRadius: "16px",
            },
          }}
        />

        <TextField
          fullWidth
          label="Dirección"
          value={form.address}
          onChange={handleChange("address")}
          sx={{
            "& .MuiOutlinedInput-root": {
              borderRadius: "16px",
            },
          }}
        />

        <Stack direction={{ xs: "column", sm: "row" }} spacing={1.2} sx={{ pt: 0.5 }}>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving}
            sx={{
              textTransform: "none",
              fontWeight: 800,
              borderRadius: "14px",
              py: 1.1,
              px: 2.4,
              color: "#ffffff",
              background:
                "linear-gradient(135deg, #5f0008 0%, #a80d19 35%, #df2632 62%, #161616 100%)",
              boxShadow: "0 18px 50px rgba(229,9,20,0.22)",
              "&:hover": {
                filter: "brightness(1.05)",
                boxShadow: "0 22px 56px rgba(229,9,20,0.26)",
              },
              "&.Mui-disabled": {
                color: "#ffffff",
                opacity: 0.75,
              },
            }}
          >
            {saving ? "Guardando..." : "Guardar cambios"}
          </Button>

          <Button
            variant="outlined"
            onClick={onCancel}
            disabled={saving}
            sx={{
              textTransform: "none",
              fontWeight: 800,
              borderRadius: "14px",
              py: 1.1,
              px: 2.4,
              color: "#111111",
              borderColor: "rgba(17,17,17,0.14)",
              "&:hover": {
                borderColor: "#e50914",
                color: "#e50914",
                bgcolor: "rgba(229,9,20,0.05)",
              },
            }}
          >
            Cancelar
          </Button>
        </Stack>
      </Stack>
    </Box>
  );
}